import { Close, CloseFullscreen, OpenInFull } from '@mui/icons-material';
import {
  Alert,
  Button,
  IconButton,
  Link,
  Snackbar,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  setEntity,
  setExpandedPanel,
  setViewPanel,
} from '../../../app/approvalQueue/approvalQueueSlice';
import {
  useGetApiApprovalQueueQuery,
  useGetApiCtpsByIdQuery,
  useGetApiDeliverablesByIdQuery,
  useGetApiPnrsByIdQuery,
  useGetApiRipsByIdQuery,
  useGetApiTarsByIdQuery,
  useGetApiTripReportsByIdQuery,
} from '../../../app/athenaApiGen';
import { useGetProjectSummaryWithAttachmentsQuery } from '../../../app/athenaApi';
import DeliverableReadOnlyView from './DeliverableReadOnlyView';
import TarReadOnlyView from './TarReadOnlyView';
import { getFormViewRoute, normalizeForViewPanel } from './viewPanelUtils';
import { RootState } from '../../../app/store';
import AuditLog from '../../../components/AuditLog/AuditLog';
import NoPermissionTooltip from '../../../components/Tooltips/NoPermissionTooltip';
import Downloads from '../../../components/ViewPanel/components/Downloads/Downloads';
import {
  ViewHeaderContainer,
  ViewPanelContainer,
  ViewPanelContentContainer,
} from '../../../components/ViewPanel/StyledComponents';
import { isActionEnabled } from '../../../pages/utilities';
import {
  useSignAndSubmit,
  type SignAndSubmitStatus,
} from '../../../signing/useSignAndSubmit';
import { useSignatureRequirement } from '../../../signing/useSignatureRequirement';
import { EntityDetailsContainer } from '../StyledComponents';
import ApproveModal from './ApproveModal';
import RejectModal from './RejectModal';
dayjs.extend(utc);

function busyLabel(status: SignAndSubmitStatus): string | undefined {
  switch (status) {
    case 'signing':
      return 'Signing…';
    case 'verifying':
      return 'Verifying…';
    case 'submitting':
    case 'submitting-signed':
      return 'Submitting…';
    default:
      return undefined;
  }
}

const ViewPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state;
  const { selectedEntity, viewPanel, expandedPanel, allowedActions } =
    useSelector((state: RootState) => state.approvalQueue);

  const objectType =
    state?.objectType ?? selectedEntity?.objectType ?? 'Unknown';

  const objectId = state?.id ?? selectedEntity?.objectId ?? '';

  const { data: deliverableData, error: deliverableError } = useGetApiDeliverablesByIdQuery(
    { id: objectId },
    { skip: !objectId || objectType !== 'Deliverable' },
  );
  const { data: projectData, error: projectError } = useGetProjectSummaryWithAttachmentsQuery(
    { id: objectId },
    { skip: !objectId || objectType !== 'Project' },
  );
  // Form-detail queries below intentionally do NOT gate on
  // `isFormTypeAvailable`. `availableForms` reflects which form types the
  // user's role can create/list from the Forms menu; approvers (Site Lead,
  // APM, COR) typically don't have those create scopes but still need to
  // fetch the form record to review it when it lands in their approval
  // queue.
  const { data: ctpData, error: ctpError } = useGetApiCtpsByIdQuery(
    { id: objectId },
    { skip: !objectId || objectType !== 'CTP' },
  );
  const { data: pnrData, error: pnrError } = useGetApiPnrsByIdQuery(
    { id: objectId },
    { skip: !objectId || objectType !== 'PNR' },
  );
  const { data: ripData, error: ripError } = useGetApiRipsByIdQuery(
    { id: objectId },
    { skip: !objectId || objectType !== 'RIP' },
  );
  const { data: tarData, error: tarError } = useGetApiTarsByIdQuery(
    { id: objectId },
    { skip: !objectId || objectType !== 'TAR' },
  );
  const { data: trData, error: trError } = useGetApiTripReportsByIdQuery(
    { id: objectId },
    { skip: !objectId || objectType !== 'TR' },
  );

  // Resolve rawData strictly by objectType. Nullish fall-through
  // (deliverableData ?? projectData ?? … ?? tarData) causes a subtle
  // stale-cache bug: when switching from a Deliverable back to a TAR,
  // the RTK Query hook that was previously live briefly retains its
  // last-selected data during re-subscription with the new args, so
  // rawData resolves to the deliverable record even though objectType
  // is now 'TAR'. normalizeForViewPanel then reads TAR-shaped fields
  // off a deliverable and produces `undefined` everywhere → blank panel.
  const rawData =
    objectType === 'Deliverable'
      ? deliverableData
      : objectType === 'Project'
        ? projectData
        : objectType === 'CTP'
          ? ctpData
          : objectType === 'PNR'
            ? pnrData
            : objectType === 'RIP'
              ? ripData
              : objectType === 'TAR'
                ? tarData
                : objectType === 'TR'
                  ? trData
                  : undefined;
  const activeError =
    deliverableError ?? projectError ?? ctpError ?? pnrError ?? ripError ?? tarError ?? trError;
  const displayData = normalizeForViewPanel(
    objectType,
    rawData,
    selectedEntity?.workflowType,
  );

  const { data: queueData } = useGetApiApprovalQueueQuery(
    { approvalState: 'all', limit: 500, page: 1 },
    { skip: !viewPanel || !objectId },
  );

  useEffect(() => {
    if (!queueData?.data || !objectId) return;
    const updatedEntity = queueData.data.find(
      (item) => item.objectId === objectId,
    );
    if (updatedEntity) {
      dispatch(setEntity(updatedEntity));
    }
  }, [queueData?.data, objectId, dispatch]);

  const user = useSelector((state: RootState) => state.user);
  const { state: signState, reset, submitApproval, submitRejection } =
    useSignAndSubmit();
  const { isSignatureRequired } = useSignatureRequirement();

  const isBusy =
    signState.status !== 'idle' &&
    signState.status !== 'success' &&
    signState.status !== 'error' &&
    signState.status !== 'wip';

  const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
  const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (signState.status === 'success') {
      setRejectModalOpen(false);
      setApproveModalOpen(false);
      reset();
    }
  }, [signState.status, reset]);

  const type = selectedEntity?.taskName?.toLowerCase().includes('internal')
    ? 'internally-approve'
    : 'federally-approve';

  const entityObjectType = selectedEntity?.objectType?.toLowerCase();
  // Approve/reject rights come from the user's role action, not from
  // whether their role can create the form via the Forms menu. Approvers
  // who can't create a TAR still need to approve one when it's in their
  // queue. Prefer the backend-supplied per-phase role action (#1301);
  // fall back to the legacy tier-derived guess for non-role-gated rows.
  const requiredAction =
    selectedEntity?.requiredRoleAction &&
    selectedEntity.requiredRoleAction !== ''
      ? selectedEntity.requiredRoleAction
      : `${type}-${entityObjectType}`;
  const isApproveEnabled = !!isActionEnabled(allowedActions, requiredAction);

  const viewFormRoute = getFormViewRoute(
    selectedEntity?.objectType,
    objectId,
    selectedEntity?.workflowType,
  );

  const isItemCompleted =
    selectedEntity?.workflowState?.toLowerCase().includes('approved') ||
    selectedEntity?.workflowState?.toLowerCase().includes('rejected');

  const handleApprove = useCallback(
    (comment: string) => {
      if (!selectedEntity?.workflowId) return;
      const trimmed = comment.trim();
      void submitApproval({
        workflowId: selectedEntity.workflowId,
        taskNumber: selectedEntity.workflowTaskNumber ?? 0,
        comments: trimmed || `Approved by ${user.profile?.userName}`,
        signatureRequired: isSignatureRequired({
          workflowType: selectedEntity.workflowType,
          taskNumber: selectedEntity.workflowTaskNumber,
        }),
      });
    },
    [
      selectedEntity?.workflowId,
      selectedEntity?.workflowTaskNumber,
      selectedEntity?.workflowType,
      user.profile?.userName,
      submitApproval,
      isSignatureRequired,
    ],
  );

  const handleReject = useCallback(
    (comment: string) => {
      if (!selectedEntity?.workflowId) return;
      void submitRejection({
        workflowId: selectedEntity.workflowId,
        taskNumber: selectedEntity.workflowTaskNumber ?? 0,
        comments: comment,
        signatureRequired: isSignatureRequired({
          workflowType: selectedEntity.workflowType,
          taskNumber: selectedEntity.workflowTaskNumber,
        }),
      });
    },
    [
      selectedEntity?.workflowId,
      selectedEntity?.workflowTaskNumber,
      selectedEntity?.workflowType,
      submitRejection,
      isSignatureRequired,
    ],
  );

  return (
    <>
      {viewPanel && (
        <ViewPanelContainer
          sx={expandedPanel ? { flex: 1 } : undefined}
        >
          <ViewHeaderContainer
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 1,
              borderBottom: '1px solid #E0E0E0',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {objectType === 'Deliverable' && !expandedPanel
                ? `Deliverable Submission - ${displayData?.title ?? ''}`
                : displayData?.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="small"
                sx={{ color: '#0092CB' }}
                onClick={() => dispatch(setExpandedPanel(!expandedPanel))}
              >
                {expandedPanel ? <CloseFullscreen /> : <OpenInFull />}
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: '#0092CB' }}
                onClick={() => {
                  dispatch(setEntity(null));
                  dispatch(setViewPanel(false));
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </ViewHeaderContainer>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              gap: 2,
            }}
          >
            {viewFormRoute ? (
              // View Form link — style mirrors the Deliverables ViewPanel.
              // Anchored on the left of the action row; Reject / Approve
              // stay right-aligned on the opposite side.
              <Link
                underline="hover"
                variant="body2"
                onClick={() => navigate(viewFormRoute)}
                sx={{
                  color: '#0092CB',
                  fontWeight: 500,
                  p: 0,
                  textTransform: 'none',
                  cursor: 'pointer',
                }}
              >
                View Form
              </Link>
            ) : (
              // Empty spacer keeps the buttons flush right when there's
              // no form link (Deliverable / Project rows).
              <span />
            )}
            {!isItemCompleted && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <NoPermissionTooltip isDisabled={!isApproveEnabled}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!isApproveEnabled || isBusy}
                    onClick={() => setRejectModalOpen(true)}
                  >
                    Reject
                  </Button>
                </NoPermissionTooltip>
                <NoPermissionTooltip isDisabled={!isApproveEnabled}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!isApproveEnabled || isBusy}
                    onClick={() => setApproveModalOpen(true)}
                  >
                    {isBusy ? (busyLabel(signState.status) ?? 'Approve') : 'Approve'}
                  </Button>
                </NoPermissionTooltip>
              </Box>
            )}
          </Box>
          <ViewPanelContentContainer sx={{ p: 2 }}>
            {activeError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Unable to load {objectType.toLowerCase()} details. The item may have been archived or deleted.
              </Alert>
            )}
            {objectType === 'TAR' && expandedPanel ? (
              // Expanded TAR view: render the full submitted form
              // (read-only) so the approver sees every field they would
              // see on the original submission — Traveler Details, Trip
              // Dates, Contract, Contacts, Location, Estimated Expenses,
              // etc. Details grid below is skipped in this mode.
              <TarReadOnlyView tarData={rawData} />
            ) : objectType === 'Deliverable' && expandedPanel ? (
              // Expanded Deliverable view: section-card layout matching
              // TAR so the two object types feel like a matched pair.
              <DeliverableReadOnlyView deliverableData={rawData} />
            ) : (
              <EntityDetailsContainer>
              {/* Title / Due Date / Type / Date Submitted are omitted for
                  TAR — the title lives in the ViewPanel header, and the
                  approver-relevant fields (Traveler / Trip Start /
                  Destination / etc.) sit in the TAR-specific cluster
                  below. Deliverable rows use their own order block
                  further down (Type → Title → Date Submitted → Due Date). */}
              {objectType !== 'TAR' && objectType !== 'Deliverable' && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2">
                      {' '}
                      {objectType} Title:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.title || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Due Date:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.dueDate
                        ? dayjs(displayData.dueDate).utc().format('DD MMM YYYY').toString()
                        : '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> {objectType} Type:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.typeOrCategory || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Date Submitted:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.dateSubmitted
                        ? dayjs(displayData.dateSubmitted)
                            .utc()
                            .format('DD MMM YYYY')
                            .toString()
                        : '-'}
                    </Typography>
                  </Box>
                </>
              )}
              {/* Deliverable-specific ordering: Type → Title → Date
                  Submitted → Due Date (per approver request). Section
                  heading mirrors the TAR "Travel Authorization Request"
                  banner for a consistent look across object types. */}
              {objectType === 'Deliverable' && (
                <>
                  <Box sx={{ gridColumn: 'span 2', mb: -1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Deliverable Submission
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Deliverable Type:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.typeOrCategory || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Deliverable Title:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.title || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Date Submitted:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.dateSubmitted
                        ? dayjs(displayData.dateSubmitted)
                            .utc()
                            .format('DD MMM YYYY')
                            .toString()
                        : '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Due Date:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.dueDate
                        ? dayjs(displayData.dueDate).utc().format('DD MMM YYYY').toString()
                        : '-'}
                    </Typography>
                  </Box>
                </>
              )}
              {/* Location skipped for TAR (Travel Destination replaces it)
                  and for Deliverable (per approver request — location isn't
                  useful at a glance for deliverable rows). */}
              {objectType !== 'TAR' && objectType !== 'Deliverable' && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2"> Location:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {displayData?.location || '-'}
                  </Typography>
                </Box>
              )}
              {objectType === 'TAR' && (
                <>
                  <Box sx={{ gridColumn: 'span 2', mb: -1 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700 }}
                    >
                      Travel Authorization Request
                    </Typography>
                  </Box>
                  {/* Order (left→right, top→bottom):
                        Traveler | Travel Destination
                        Trip Start | Trip End
                        # of Nights | # of Days
                        Project | CLIN
                        Total Estimated Trip Cost (spans 2) */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Traveler:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.tar?.travelerName || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Destination:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.tar?.travelDestination || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Trip Start:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.tar?.tripStart
                        ? dayjs(displayData.tar.tripStart)
                            .utc()
                            .format('DD MMM YYYY')
                            .toString()
                        : '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Trip End:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.tar?.tripEnd
                        ? dayjs(displayData.tar.tripEnd)
                            .utc()
                            .format('DD MMM YYYY')
                            .toString()
                        : '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> # of Nights:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.tar?.numberOfNights || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> # of Days:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.tar?.numberOfDays || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> Project:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.tar?.projectName || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2"> CLIN:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.tar?.clin || '-'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gridColumn: 'span 2',
                    }}
                  >
                    <Typography variant="subtitle2">
                      Total Estimated Trip Cost:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {displayData?.tar?.totalEstimatedCost || '-'}
                    </Typography>
                  </Box>
                </>
              )}
              </EntityDetailsContainer>
            )}
            {displayData?.attachments && (
              <Downloads
                attachments={displayData.attachments}
                zipFileName={`${displayData.title}_attachments.zip`}
              />
            )}
            <Box sx={{ mt: 3 }}>
              <AuditLog auditLog={selectedEntity?.auditLog || []} />
            </Box>
          </ViewPanelContentContainer>
        </ViewPanelContainer>
      )}
      <RejectModal
        open={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          reset();
        }}
        onConfirm={handleReject}
        isLoading={isBusy}
        busyLabel={busyLabel(signState.status)}
      />
      <ApproveModal
        open={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          reset();
        }}
        onConfirm={handleApprove}
        isLoading={isBusy}
        busyLabel={busyLabel(signState.status)}
      />
      {/* Approval/signing is still demo scaffolding, so any approve/reject
          error surfaces a friendly "in development" toast (filled Alert,
          top-center — the app-wide Snackbar convention) rather than a raw
          signing error. */}
      <Snackbar
        open={signState.status === 'error'}
        autoHideDuration={2000}
        onClose={reset}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={reset} variant="filled">
          This feature is currently in development.
        </Alert>
      </Snackbar>
      <Snackbar
        open={signState.status === 'wip'}
        autoHideDuration={6000}
        onClose={reset}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={reset} variant="filled">
          Work in Progress
        </Alert>
      </Snackbar>
    </>
  );
};

export default ViewPanel;
